import { IndeterminateProgressBar } from 'cdk-radial';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { EVENT_NAME } from 'common/constants';
import { Dialog, Toast } from 'components';
import { useAmplitude } from 'components/AmplitudeProvider';
import { usePrintChecks, usePrintSummarySheet } from 'hooks';
import { errorMessages } from 'hooks/messages';
import {
  getPaymentDate,
  getPostingDescription,
  getPrintChecksPayload,
  getPrinterName,
  getPrintOrder,
  getSelectedCIBAccount,
  getSelectedVendors,
  getTotalAmount,
  setCheckWritingResponse,
  setIsPrintChecksDialogOpen,
  setIsPrintFailureDialogOpen,
  setIsPrintSuccessDialogOpen,
  setPostingDescription,
  setPrintOrder
} from 'pages/VendorsTablePage/components/PrintChecks/slice';
import { usePrintChecksTableColDefs } from 'pages/VendorsTablePage/components/PrintChecks/usePrintChecksTableColDefs';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import { formatLocaleDate } from 'utils/dateUtils';
import { PROGRESS_BAR_WIDTH } from '../constants';
import { DialogContent } from './DialogContent';
import { labels } from './messages';
import { PostingDescription } from './PostingDescription';
import { StyledToast } from './styled';
import { PrintChecksDialogProps } from './types';

export const PrintChecksDialog = ({
  open
}: PrintChecksDialogProps): React.JSX.Element => {
  const amplitude = useAmplitude();
  const [isPrintChecksError, setIsPrintChecksError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const selectedVendors = useAppSelector(getSelectedVendors);
  const paymentDate = useAppSelector(getPaymentDate);
  const totalAmount = useAppSelector(getTotalAmount);
  const { postingDescriptionRequired } = useAppSelector(getSelectedCIBAccount);
  const postingDescription = useAppSelector(getPostingDescription);
  const printOrder = useAppSelector(getPrintOrder);
  const printerName = useAppSelector(getPrinterName);
  const printChecksPayload = useAppSelector(getPrintChecksPayload);
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const columnDefs = usePrintChecksTableColDefs();
  const { printSummarySheetTitle } = labels;
  const { openPrintSummarySheet } = usePrintSummarySheet({
    intl,
    printSummarySheetTitle,
    columnDefs,
    selectedVendors
  });

  const {
    data,
    error,
    isLoading,
    mutate: printChecks,
    status
  } = usePrintChecks();
  const handleCloseDialog = useCallback(() => {
    setPostingDescription('');
    dispatch(setIsPrintChecksDialogOpen(false));
  }, [dispatch]);

  const handlePrintChecks = useCallback(() => {
    amplitudeTracker(amplitude, EVENT_NAME.PrintChecks, {
      'Vendor Total': printChecksPayload.vendors.length || 0,
      'Invoice Total': printChecksPayload.vendors.reduce(
        (acc, checks) => acc + checks.invoices.length,
        0
      )
    });
    if (!postingDescriptionRequired || postingDescription) {
      printChecks(printChecksPayload);
    }
  }, [
    amplitude,
    printChecks,
    postingDescription,
    postingDescriptionRequired,
    printChecksPayload
  ]);

  useEffect(() => {
    if (data) {
      const { printOkList, printFailureList } = data;
      dispatch(setCheckWritingResponse(data));
      if (printOkList.length > 0) dispatch(setIsPrintSuccessDialogOpen(true));
      if (printOkList.length === 0 && printFailureList.length > 0)
        dispatch(setIsPrintFailureDialogOpen(true));
      handleCloseDialog();
    }
  }, [data, dispatch, handleCloseDialog]);

  useEffect(() => {
    if (status === 'error') {
      setErrorMessage(
        error?.message.replace('Error: ', '') ||
          intl.formatMessage(errorMessages.printChecksApiError)
      );
      setIsPrintChecksError(true);
    }
  }, [error, intl, status]);

  const handleOptionSelect = (event: { target: { value: string } }) => {
    const {
      target: { value }
    } = event;
    dispatch(setPrintOrder(value));
  };

  const handlePostingDescription = (value: string) =>
    dispatch(setPostingDescription(value));

  const printDialogTitle =
    selectedVendors.length > 1 ? labels.checksLabel : labels.checkLabel;

  const handlePrintSummarySheet = useCallback(() => {
    openPrintSummarySheet();
  }, [openPrintSummarySheet]);

  const buttonsForDialog = useMemo(
    () => [
      {
        id: 'print-summary-sheet',
        onClick: handlePrintSummarySheet,
        text: intl.formatMessage(labels.printSummarySheetLabel),
        variant: 'text'
      },
      {
        id: 'print-checks',
        isDisabled: isLoading,
        onClick: handlePrintChecks,
        text: intl.formatMessage(labels.nextPrintChecks),
        variant: 'text'
      }
    ],
    [handlePrintChecks, handlePrintSummarySheet, intl, isLoading]
  );

  const radioOptions = [
    {
      id: 'vendorName',
      label: intl.formatMessage(labels.vendorLabel),
      value: 'vendorName'
    },
    {
      id: 'vendorNo',
      label: intl.formatMessage(labels.vendorNumber),
      value: 'vendorNo'
    }
  ];

  const handleDismiss = () => setIsPrintChecksError(false);
  return (
    <div>
      <Dialog
        buttonsProps={buttonsForDialog}
        handleClose={handleCloseDialog}
        title={`${intl.formatMessage(labels.printOptions)} - ${
          selectedVendors.length
        } ${intl.formatMessage(printDialogTitle)}`}
        open={open}
        dataTestId="print-checks-dialog"
        id="print-checks-dialog"
      >
        {isPrintChecksError && (
          <StyledToast>
            <Toast
              content={errorMessage}
              buttonProps={[
                {
                  text: intl.formatMessage(labels.dismiss),
                  onClick: () => handleDismiss()
                }
              ]}
            />
          </StyledToast>
        )}
        <PostingDescription
          handlePostingDescription={handlePostingDescription}
          postingDescription={postingDescription}
        />
        {isLoading && (
          <IndeterminateProgressBar
            dataTestId="indeterminate"
            label=""
            hideLabel
            trackWidth={PROGRESS_BAR_WIDTH}
          />
        )}

        <DialogContent
          paymentDate={formatLocaleDate(paymentDate, intl.locale)}
          totalAmount={totalAmount}
          handleOptionSelect={handleOptionSelect}
          printOrder={printOrder}
          radioOptions={radioOptions}
          printerName={printerName}
        />
      </Dialog>
    </div>
  );
};
